/**
 * CartManager
 * Client-side cart state singleton (per merchant) with localStorage + Redis sync.
 *
 * Cart Rules:
 * 1. Only products of the same type can be in the same cart
 * 2. Only one digital product is allowed per cart
 */

import type {
  Product,
  ProductVariant,
  LocalCartItem,
  ProductType,
  CartValidationError,
} from '../types';
import type { ApiClient } from './api-client';

const CART_EXPIRY_DAYS = 14;
const SYNC_DEBOUNCE_MS = 500;
const HYDRATE_STALE_MS = 5 * 60 * 1000; // 5 minutes

function generateCartId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback UUID v4
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * The option ids a cart line sends to the server.
 *
 * `variant_id` on a line is the option the shopper picked (a sub-option when
 * there is one). Checkout matches `least_sub_variant_id` (that picked option)
 * and `main_variant` (its top-level option); the server cart stores the
 * top-level option as `variant_id` and the sub-option as `sub_variant_id`.
 */
export function cartLineVariantIds(item: Pick<LocalCartItem, 'variant_id' | 'main_variant_id'>): {
  main_variant?: string;
  least_sub_variant_id?: string;
  variant_id?: string;
  sub_variant_id?: string;
} {
  if (item.variant_id == null || item.variant_id === '') return {};
  const leaf = String(item.variant_id);
  const top = item.main_variant_id != null && item.main_variant_id !== '' ? String(item.main_variant_id) : leaf;
  return {
    main_variant: top,
    least_sub_variant_id: leaf,
    variant_id: top,
    ...(top !== leaf ? { sub_variant_id: leaf } : {}),
  };
}

function storageKey(merchantId: string | number): string {
  return `khaime:cart:${merchantId}`;
}

interface StoredCart {
  cart_id: string;
  items: LocalCartItem[];
  product_type: ProductType | null;
  currency: string;
  updated_at: number;
}

type CartListener = (items: LocalCartItem[]) => void;
type CartErrorListener = (error: CartValidationError) => void;

export class CartManager {
  private static instances = new Map<string, CartManager>();

  private merchantId: string | number;
  private cartId: string;
  private items: LocalCartItem[] = [];
  private productType: ProductType | null = null;
  private currency: string = 'USD';
  private updatedAt: number = 0;
  private api: ApiClient;
  private listeners = new Set<CartListener>();
  /** Told about each add, for Store Pulse (set by the SDK). */
  private onAdd: ((item: LocalCartItem, quantity: number) => void) | null = null;
  private errorListeners = new Set<CartErrorListener>();
  private syncTimer: ReturnType<typeof setTimeout> | null = null;
  private debug: boolean;
  /**
   * Monotonic token used to ignore stale reconcile responses when the user
   * flips currency faster than the network can round-trip.
   */
  private currencyReconcileToken = 0;

  static getInstance(merchantId: string | number, api: ApiClient, debug = false): CartManager {
    const key = String(merchantId);
    let instance = CartManager.instances.get(key);
    if (!instance) {
      instance = new CartManager(merchantId, api, debug);
      CartManager.instances.set(key, instance);
    }
    return instance;
  }

  static resetAll(): void {
    CartManager.instances.clear();
  }

  private constructor(merchantId: string | number, api: ApiClient, debug: boolean) {
    this.merchantId = merchantId;
    this.api = api;
    this.debug = debug;
    this.cartId = '';

    this.loadFromLocalStorage();
  }

  // -------------------------------------------------------
  // Validation Methods
  // -------------------------------------------------------

  /**
   * Validate if a product can be added to the cart.
   * Rules:
   * 1. Only physical products can be added to cart (others use direct checkout)
   * 2. Only products of the same type can be in the cart
   * 3. Only one digital product is allowed per cart
   */
  private validateAddItem(product: Product): CartValidationError | null {
    // Only physical products belong in the cart — all other types use direct checkout
    if (product.type !== 'physical_product') {
      return {
        code: 'CART_ONLY_PHYSICAL',
        message: `Only physical products can be added to cart. Use direct checkout for ${product.type} products.`,
      };
    }

    return null;
  }

  /**
   * Check if a product can be added to the cart
   */
  canAddItem(product: Product): { valid: boolean; error?: CartValidationError } {
    const error = this.validateAddItem(product);
    return error ? { valid: false, error } : { valid: true };
  }

  // -------------------------------------------------------
  // Core Operations
  // -------------------------------------------------------

  /**
   * Add a product to the cart.
   * Returns an error if the product cannot be added due to cart rules.
   */
  addItem(
    product: Product,
    quantity: number = 1,
    variant?: ProductVariant,
    subscriptionFrequencyKey?: string
  ): CartValidationError | null {
    // Validate before adding
    const validationError = this.validateAddItem(product);
    if (validationError) {
      this.notifyError(validationError);
      return validationError;
    }

    // A cart holds one currency. Products priced in another (the shopper
    // switched, or the store sells in one currency only) move the cart to it
    // first, so a KES price is never labelled USD.
    this.adoptCurrency(product.currency);

    const variantId = variant?.id;
    const mainVariantId =
      variant?.variation_id != null && String(variant.variation_id) !== String(variant.id)
        ? variant.variation_id
        : undefined;
    const existingIndex = this.items.findIndex(
      i => i.product_id === product.id && i.variant_id === variantId
    );

    // For digital products, don't allow increasing quantity beyond 1
    const effectiveQuantity = product.type === 'digital' ? 1 : quantity;

    // Calculate price - use variant price if available, otherwise product price with slash price logic
    // If price is 0 or missing, try to parse from formatted_price as fallback
    let itemPrice =
      variant?.price ??
      (product.is_slash_price && product.slash_price ? product.slash_price : product.price) ??
      0;

    // If price is still 0, try to extract from formatted_price
    if (itemPrice === 0 && product.formatted_price) {
      itemPrice = this.parseFormattedPrice(product.formatted_price);
      this.log('Parsed price from formatted_price:', product.formatted_price, '->', itemPrice);
    }

    // Get thumbnail - fallback to first image if thumbnail is not set
    const itemThumbnail = product.thumbnail || (product.images && product.images[0]) || null;

    // Get formatted prices: an option carries its own full price.
    const formattedPrice = variant ? (variant.formatted_price ?? undefined) : product.formatted_price;
    const formattedOriginalPrice = !variant && product.is_slash_price
      ? product.formatted_slash_price
      : undefined;

    if (existingIndex >= 0) {
      if (product.type === 'digital') {
        // Digital products can only have quantity 1
        this.items[existingIndex].quantity = 1;
      } else {
        this.items[existingIndex].quantity += effectiveQuantity;
      }
      // Update price and thumbnail in case product data changed
      this.items[existingIndex].price = itemPrice;
      this.items[existingIndex].formatted_price = formattedPrice;
      this.items[existingIndex].thumbnail = itemThumbnail;
    } else {
      this.items.push({
        product_id: product.id,
        title: product.title,
        thumbnail: itemThumbnail,
        type: product.type,
        quantity: effectiveQuantity,
        price: itemPrice,
        formatted_price: formattedPrice,
        original_price: !variant && product.is_slash_price ? product.price : undefined,
        formatted_original_price: formattedOriginalPrice,
        variant_id: variantId,
        ...(mainVariantId != null ? { main_variant_id: mainVariantId } : {}),
        variant_name: variant?.name,
        subscription_frequency_key: subscriptionFrequencyKey,
        currency: this.currency,
        added_at: Date.now(),
      });
    }

    // Set cart product type
    if (!this.productType) {
      this.productType = product.type;
    }

    if (!this.cartId) {
      this.cartId = generateCartId();
    }

    this.persist('add');
    const added = existingIndex >= 0 ? this.items[existingIndex] : this.items[this.items.length - 1];
    if (added && this.onAdd) this.onAdd(added, effectiveQuantity);
    return null;
  }

  /** Called with each item added; the SDK reports it to Store Pulse. */
  setAddListener(listener: ((item: LocalCartItem, quantity: number) => void) | null): void {
    this.onAdd = listener;
  }

  updateQuantity(productId: number, quantity: number, variantId?: number | string): void {
    if (quantity <= 0) {
      this.removeItem(productId, variantId);
      return;
    }

    const item = this.items.find(
      i => i.product_id === productId && (variantId == null || i.variant_id === variantId)
    );

    if (item) {
      item.quantity = quantity;
      this.persist('update');
    }
  }

  removeItem(productId: number, variantId?: number | string): void {
    const initialLength = this.items.length;
    this.items = this.items.filter(
      i => !(i.product_id === productId && (variantId == null || i.variant_id === variantId))
    );

    if (this.items.length !== initialLength) {
      // Reset product type if cart is now empty
      if (this.items.length === 0) {
        this.productType = null;
      }
      this.persist('remove');
    }
  }

  clearCart(): void {
    this.items = [];
    this.productType = null;
    this.persist('clear');

    if (this.cartId) {
      this.api.clearCart(this.cartId).catch(err => {
        this.log('Failed to clear cart on server:', err);
      });
    }
  }

  getItems(): LocalCartItem[] {
    return [...this.items];
  }

  getItemCount(): number {
    return this.items.reduce((sum, item) => sum + item.quantity, 0);
  }

  /**
   * Sum of all cart line totals in MINOR units (cents, kobo, pesewas).
   *
   * `item.price` is stored in minor units at add-time — it comes from the
   * backend-returned `Product.price` field, which ships in minor units
   * across the `/sdk/storefront/*` surface. Consumers that need to display
   * the subtotal should divide by 100 (or the currency's decimal count) at
   * the formatting boundary, not here.
   */
  getSubtotalMinor(): number {
    return this.items.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0);
  }

  /**
   * @deprecated Use `getSubtotalMinor()` — the name `getSubtotal` is
   *   ambiguous about units and the old implementation's rounding gave
   *   the impression it returned major units (dollars). Kept as a thin
   *   alias so any external consumers don't break during migration.
   */
  getSubtotal(): number {
    return this.getSubtotalMinor();
  }

  getCartId(): string {
    return this.cartId;
  }

  getCurrency(): string {
    return this.currency;
  }

  /**
   * Get the product type of items in the cart.
   * Returns null if cart is empty.
   */
  getProductType(): ProductType | null {
    return this.productType;
  }

  /**
   * Check if cart contains physical products (requires shipping)
   */
  requiresShipping(): boolean {
    return this.productType === 'physical_product';
  }

  /**
   * Check if cart contains digital products
   */
  isDigitalCart(): boolean {
    return this.productType === 'digital';
  }

  /**
   * Check if cart contains subscription products
   */
  isSubscriptionCart(): boolean {
    return this.productType === 'subscription';
  }

  /**
   * Check if cart contains event products
   */
  isEventCart(): boolean {
    return this.productType === 'live_event';
  }

  /**
   * Build cart payload for early abandonment capture (flexible validation).
   * Used when customer submits their info before shipping.
   */
  buildEarlyCapturePayload(customer: {
    email: string;
    name?: string;
    phone?: string;
  }): {
    cart: Array<{
      product_id: number;
      quantity: number;
      price?: number;
      has_variation?: boolean;
      main_variant?: string | number;
      least_sub_variant_id?: string | number;
    }>;
    customer: { email: string; name?: string; phone?: string };
    currency: string;
  } {
    return {
      cart: this.items.map(item => {
        const ids = cartLineVariantIds(item);
        return {
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.price,
          has_variation: !!item.variant_id,
          main_variant: ids.main_variant,
          least_sub_variant_id: ids.least_sub_variant_id,
        };
      }),
      customer,
      currency: this.currency,
    };
  }

  /**
   * Build cart payload for full shipping validation (strict validation).
   * Used when customer submits shipping address.
   */
  buildFullValidationPayload(
    shipping: {
      name: string;
      address: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
      countryCode: string;
    },
    customer: {
      email?: string;
      phone?: string;
    },
    cartUniqueId?: string
  ): {
    cart: Array<{
      product_id: number;
      quantity: number;
      has_variation: boolean;
      main_variant?: string;
      least_sub_variant_id?: string;
      price: number;
      product_image: string;
      product_variant_data: string;
      subscription_frequency_key?: string;
    }>;
    delivery_details: {
      save_for_future_checkout: boolean;
      store_pick_up: boolean;
      national_delivery: boolean;
      international_delivery: boolean;
      address: Array<{
        name: string;
        address: string;
        city: string;
        state: string;
        zip_code: string;
        country: string;
        country_code: string;
        email?: string;
        phone_number?: string;
      }>;
    };
    currency: string;
    cart_unique_id?: string;
    coupons: string[];
  } {
    return {
      cart: this.items.map(item => {
        const hasVariation = !!item.variant_id;
        const image = typeof item.thumbnail === 'string' ? item.thumbnail : '';
        return {
          product_id: item.product_id,
          quantity: item.quantity,
          has_variation: hasVariation,
          price: item.price,
          product_image: image,
          product_variant_data: hasVariation
            ? (item.variant_name || `Quantity: ${item.quantity}`)
            : `Quantity: ${item.quantity}`,
          ...(hasVariation ? {
            main_variant: cartLineVariantIds(item).main_variant,
            least_sub_variant_id: cartLineVariantIds(item).least_sub_variant_id,
          } : {}),
          ...(item.subscription_frequency_key ? {
            subscription_frequency_key: item.subscription_frequency_key,
          } : {}),
        };
      }),
      delivery_details: {
        save_for_future_checkout: false,
        store_pick_up: false,
        national_delivery: shipping.countryCode === 'US',
        international_delivery: shipping.countryCode !== 'US',
        address: [
          {
            name: shipping.name,
            address: shipping.address,
            city: shipping.city,
            state: shipping.state,
            zip_code: shipping.zipCode,
            country: shipping.country,
            country_code: shipping.countryCode,
            email: customer.email,
            phone_number: customer.phone,
          },
        ],
      },
      currency: this.currency,
      cart_unique_id: cartUniqueId,
      coupons: [], // TODO: Add coupon support
    };
  }

  /**
   * Switch the cart's active currency and ask the server to reprice items.
   *
   * Order of operations is deliberate:
   *   1. Bump `this.currency` immediately so new items added mid-flight use
   *      the right currency on their outbound requests.
   *   2. Leave each `item.currency` + `item.price` at their previous values
   *      until the server's repriced list arrives. This avoids a flash
   *      where the UI labels items as (say) NGN but still shows USD cents.
   *   3. When reconcile lands, `reconcileWithApi` atomically updates each
   *      `price`/`formatted_price`/`currency` together.
   *
   * Rapid switches (USD→NGN→GBP in quick succession) use a monotonic
   * token so an earlier reconcile can't overwrite the latest selection.
   * On reconcile failure we emit a `CURRENCY_RECONCILE_FAILED` error so
   * the embedding UI can warn the customer and/or retry.
   */
  setCurrency(currency: string): Promise<void> {
    if (!currency || currency === this.currency) return Promise.resolve();

    this.currency = currency;
    this.saveToLocalStorage();
    // Notify so host UIs re-read the new currency label — items keep their
    // old prices + old currency internally until reconcile arrives, which
    // is the correct intermediate state.
    this.notify();

    if (!this.cartId || this.items.length === 0) return Promise.resolve();

    // Resolves once the cart is repriced (or the reprice failed), so a caller
    // that awaits it never reads a subtotal that adds the old currency's
    // prices to the new one's.
    const token = ++this.currencyReconcileToken;
    return this.api
      .getCart(this.cartId, currency)
      .then(response => {
        // A newer switch already overrode this one — drop the stale response.
        if (token !== this.currencyReconcileToken) return;
        this.reconcileWithApi(response.items);
      })
      .catch(err => {
        if (token !== this.currencyReconcileToken) return;
        this.log('Failed to reprice cart for currency switch:', err);
        this.notifyError({
          code: 'CURRENCY_RECONCILE_FAILED',
          message: `Could not reprice cart in ${currency}. Prices may be out of date.`,
        });
      });
  }

  /**
   * Move the cart to the currency products are priced in. An empty cart just
   * takes the new label; a cart with items is repriced by the server
   * (`setCurrency`). Called when products load and before an item is added.
   */
  adoptCurrency(currency: string | null | undefined): void {
    const code = (currency || '').trim().toUpperCase();
    if (!code || code === this.currency) return;
    if (this.items.length === 0) {
      this.currency = code;
      this.saveToLocalStorage();
      this.notify();
      return;
    }
    this.setCurrency(code);
  }

  // -------------------------------------------------------
  // Subscriptions
  // -------------------------------------------------------

  subscribe(listener: CartListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  subscribeToErrors(listener: CartErrorListener): () => void {
    this.errorListeners.add(listener);
    return () => {
      this.errorListeners.delete(listener);
    };
  }

  private notify(): void {
    const items = this.getItems();
    for (const listener of this.listeners) {
      try {
        listener(items);
      } catch (err) {
        this.log('Listener error:', err);
      }
    }
  }

  private notifyError(error: CartValidationError): void {
    for (const listener of this.errorListeners) {
      try {
        listener(error);
      } catch (err) {
        this.log('Error listener error:', err);
      }
    }
  }

  // -------------------------------------------------------
  // Persistence
  // -------------------------------------------------------

  private persist(action: 'add' | 'remove' | 'update' | 'clear'): void {
    this.updatedAt = Date.now();
    this.saveToLocalStorage();
    this.notify();
    this.debouncedSync();
    this.log(`Cart ${action}:`, this.items.length, 'items');
  }

  private saveToLocalStorage(): void {
    try {
      const data: StoredCart = {
        cart_id: this.cartId,
        items: this.items,
        product_type: this.productType,
        currency: this.currency,
        updated_at: this.updatedAt,
      };
      localStorage.setItem(storageKey(this.merchantId), JSON.stringify(data));
    } catch (err) {
      this.log('Failed to save to localStorage:', err);
    }
  }

  private loadFromLocalStorage(): void {
    try {
      const raw = localStorage.getItem(storageKey(this.merchantId));
      if (!raw) return;

      const data: StoredCart = JSON.parse(raw);

      // Check expiry (14 days)
      const age = Date.now() - data.updated_at;
      if (age > CART_EXPIRY_DAYS * 24 * 60 * 60 * 1000) {
        localStorage.removeItem(storageKey(this.merchantId));
        return;
      }

      this.cartId = data.cart_id;
      this.items = data.items;
      this.productType = data.product_type || null;
      this.currency = data.currency;
      this.updatedAt = data.updated_at;
    } catch (err) {
      this.log('Failed to load from localStorage:', err);
    }
  }

  private debouncedSync(): void {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
    }
    this.syncTimer = setTimeout(() => {
      this.syncToRedis();
    }, SYNC_DEBOUNCE_MS);
  }

  private async syncToRedis(): Promise<void> {
    if (!this.cartId || this.items.length === 0) return;

    try {
      const results = await Promise.allSettled(
        this.items.map(item =>
          this.api.addToCart(this.cartId, {
            product_id: item.product_id,
            quantity: item.quantity,
            variant_id: cartLineVariantIds(item).variant_id,
            sub_variant_id: cartLineVariantIds(item).sub_variant_id,
            currency: this.currency,
          })
        )
      );

      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length > 0) {
        this.log(`Synced to Redis with ${failed.length} failed item(s)`);
      } else {
        this.log('Synced to Redis');
      }
    } catch (err) {
      this.log('Failed to sync to Redis:', err);
    }
  }

  /**
   * Hydrate cart from API if stale.
   * Called on storefront mount.
   */
  async hydrate(): Promise<void> {
    if (!this.cartId || this.items.length === 0) return;

    const age = Date.now() - this.updatedAt;
    if (age < HYDRATE_STALE_MS) {
      this.log('Cart is fresh, skipping hydration');
      return;
    }

    try {
      const response = await this.api.getCart(this.cartId, this.currency);
      this.reconcileWithApi(response.items);

      if (response.removed_items && response.removed_items.length > 0) {
        this.log('Removed unavailable products:', response.removed_items);
      }
    } catch (err: any) {
      if (err?.message?.includes('404')) {
        // Cart expired in Redis, reset
        this.cartId = generateCartId();
        this.saveToLocalStorage();
        this.log('Cart expired on server, generated new cart ID');
      } else {
        this.log('Hydration failed:', err);
      }
    }
  }

  private reconcileWithApi(
    apiItems: Array<{
      product_id: number;
      price: number;
      formatted_price?: string;
      original_price?: number;
      formatted_original_price?: string;
      title: string;
      thumbnail: string | null;
      variant_name?: string;
      [key: string]: any;
    }>
  ): void {
    const apiMap = new Map<string, (typeof apiItems)[0]>();
    for (const item of apiItems) {
      // The server cart keeps the top-level option in variant_id and the
      // sub-option in sub_variant_id; a local line's variant_id is the picked one.
      apiMap.set(`${item.product_id}:${item.sub_variant_id || item.variant_id || ''}`, item);
    }

    // Update local items with fresh data from API
    const updated: LocalCartItem[] = [];
    for (const local of this.items) {
      const key = `${local.product_id}:${local.variant_id || ''}`;
      const api = apiMap.get(key);
      if (!api) {
        // Added in the new currency while the server was repricing: keep it.
        if (local.currency === this.currency) updated.push(local);
        continue; // Otherwise the product was removed or is unavailable
      }

      updated.push({
        ...local,
        price: api.price,
        formatted_price: api.formatted_price,
        original_price: api.original_price,
        formatted_original_price: api.formatted_original_price,
        title: api.title,
        thumbnail: api.thumbnail,
        variant_name: api.variant_name || local.variant_name,
        currency: this.currency,
      });
    }
    this.items = updated;

    this.updatedAt = Date.now();
    this.saveToLocalStorage();
    this.notify();
  }

  private log(...args: unknown[]): void {
    if (this.debug) {
      console.log('[Khaime Cart]', ...args);
    }
  }

  /**
   * Parse a formatted price string like "$100.00" or "₦50,000" into cents.
   * Returns the amount in cents (integer).
   */
  private parseFormattedPrice(formatted: string): number {
    if (!formatted) return 0;

    // Remove currency symbols and non-numeric characters except . and ,
    const cleaned = formatted.replace(/[^0-9.,]/g, '');

    // Handle different number formats (1,000.00 vs 1.000,00)
    let numericStr = cleaned;

    // If we have both . and ,, determine which is the decimal separator
    if (cleaned.includes('.') && cleaned.includes(',')) {
      const lastDot = cleaned.lastIndexOf('.');
      const lastComma = cleaned.lastIndexOf(',');

      if (lastDot > lastComma) {
        // Format: 1,000.00 (dot is decimal)
        numericStr = cleaned.replace(/,/g, '');
      } else {
        // Format: 1.000,00 (comma is decimal)
        numericStr = cleaned.replace(/\./g, '').replace(',', '.');
      }
    } else if (cleaned.includes(',')) {
      // Could be 1,000 (thousand separator) or 100,00 (decimal)
      const parts = cleaned.split(',');
      if (parts.length === 2 && parts[1].length === 2) {
        // Likely decimal format: 100,00
        numericStr = cleaned.replace(',', '.');
      } else {
        // Likely thousand separator: 1,000
        numericStr = cleaned.replace(/,/g, '');
      }
    }

    const amount = parseFloat(numericStr);
    if (isNaN(amount)) return 0;

    // Convert to cents (multiply by 100)
    return Math.round(amount * 100);
  }
}
